import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reportsApi } from '../utils/api';

/**
 * Client-facing security assessment report, rendered inside the authenticated
 * portal. The report HTML is a self-contained snapshot from the backend; we drop
 * it into an isolated iframe so its inline styles can't leak into the portal.
 */
export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await reportsApi.get(id);
        if (!cancelled) setReport(d);
      } catch (e) {
        if (!cancelled) setError('This report is not available.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Print just the report (the iframe is its own document, so its print output
  // is the clean report with no portal chrome). Force every collapsible section
  // open first so nothing is hidden in the PDF. "Save as PDF" is the print
  // destination the client picks — works in every modern browser.
  const downloadPdf = () => {
    const frame = document.getElementById('report-frame');
    const win = frame && frame.contentWindow;
    if (!win) return;
    try {
      win.document.querySelectorAll('details').forEach((d) => { d.open = true; });
    } catch (_) { /* same-origin srcDoc; ignore if unavailable */ }
    win.focus();
    win.print();
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <button
          onClick={() => navigate('/reports')}
          style={{ background: 'none', border: 'none', color: '#068dac', cursor: 'pointer', fontSize: 14, padding: '4px 0' }}
        >
          ← All reports
        </button>
        {report && (
          <button
            onClick={downloadPdf}
            style={{ marginLeft: 'auto', background: '#068dac', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '8px 16px' }}
            title="Save this report as a PDF for your records"
          >
            ⬇ Download PDF
          </button>
        )}
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading your report…</div>}
      {error && <div style={{ padding: 40, textAlign: 'center', color: '#c0392b' }}>{error}</div>}

      {report && (
        <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <iframe
            id="report-frame"
            title={report.title || 'Security Assessment'}
            srcDoc={report.html}
            style={{ width: '100%', height: '80vh', border: 'none', display: 'block' }}
          />
        </div>
      )}
    </div>
  );
}
